// כרטיס וריאציה במרכז היצוא: בדיקות, אישור אזהרות, קישור נעול, יצוא (טיוטה/מסירה/טסט), תוצאה והיסטוריה.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { missingRequired, SPEC_FIELD_LABELS } from '@bb/schema';
import { api, waitForJob, type Job } from '../../api';
import { ErrorBox, fmtDate, fmtKb, JobBadge, useMe } from '../ui';
import VariantThumb, { useVariantCtx } from '../editor/VariantThumb';
import { SPEC_STATUS_LABEL, type Check } from '../editor/types';
import ChecksView from './ChecksView';
import ExportResult, { KIND_LABEL, XSTATUS, type ExportRow } from './ExportResult';

export interface LinkRow { variantId: string; resolvedUrl: string | null; baseUrl: string; status: string; issues: any[]; conflicts?: any[]; bannerKey: string }
interface Validation { checks: Check[]; summary: { errors: number; warnings: number; passed: number }; revision: number; resolvedUrl: string | null }

interface Props {
  variant: { id: string; width: number; height: number; display_name: string; media_name: string; placement_name: string; current_revision: number };
  link: LinkRow | null;
  onWarnings: (variantId: string, codes: string[]) => void;
}

export default function VariantExportCard({ variant: v, link, onWarnings }: Props) {
  const me = useMe();
  const { ctx } = useVariantCtx(v.id, v.current_revision);
  const [val, setVal] = useState<Validation | null>(null);
  const [vErr, setVErr] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [acks, setAcks] = useState<string[]>([]);
  const [testUrl, setTestUrl] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const [xErr, setXErr] = useState<string | null>(null);
  const [result, setResult] = useState<ExportRow | null>(null);
  const [history, setHistory] = useState<ExportRow[] | null>(null);
  const [open, setOpen] = useState<ExportRow | null>(null);
  // מפתח idempotency לכל לחיצה; נשמר לניסיון חוזר אחרי כשל רשת כדי שלא ייווצר יצוא כפול
  const pendingKey = useRef<Record<string, string>>({});

  const validate = useCallback(async () => {
    setChecking(true); setVErr(null);
    try {
      const r = await api.post<Validation>(`/api/variants/${v.id}/validate`, {});
      setVal(r);
      const codes = [...new Set(r.checks.filter((c) => !c.passed && c.severity === 'warning').map((c) => c.code))];
      onWarnings(v.id, codes);
      setAcks((a) => a.filter((x) => codes.includes(x)));
    } catch (e: any) { setVErr(e.message); } finally { setChecking(false); }
  }, [v.id, onWarnings]);
  const loadHistory = useCallback(() => { api.get<ExportRow[]>(`/api/exports?variantId=${v.id}`).then(setHistory).catch(() => setHistory([])); }, [v.id]);
  useEffect(() => { void validate(); loadHistory(); }, [validate, loadHistory]);

  const spec = ctx?.spec;
  const approved = spec?.status === 'approved';
  const missing = spec ? missingRequired(spec.resolved as any) : [];
  const warnCodes = val ? [...new Set(val.checks.filter((c) => !c.passed && c.severity === 'warning').map((c) => c.code))] : [];
  const unacked = warnCodes.filter((c) => !acks.includes(c));
  const deliveryBlock: string[] = [];
  if (!me.canExportDelivery) deliveryBlock.push('אין לך הרשאת יצוא למסירה');
  if (spec && !approved) deliveryBlock.push('המפרט אינו מאושר — טיוטה בלבד');
  if (missing.length) deliveryBlock.push(`שדות חובה לא ידועים: ${missing.map((k) => SPEC_FIELD_LABELS[k]).join(', ')}`);
  if (!val) deliveryBlock.push('יש להריץ בדיקות');
  else if (val.summary.errors > 0) deliveryBlock.push(`${val.summary.errors} שגיאות חוסמות`);
  if (unacked.length) deliveryBlock.push(`יש לאשר ${unacked.length} אזהרות`);
  if (link && link.status !== 'ok') deliveryBlock.push('הקישור המתויג דורש טיפול');

  const runExport = async (kind: 'draft' | 'delivery' | 'test') => {
    setBusy(true); setXErr(null); setJob(null); setResult(null);
    try {
      const sig = `${kind}|${val?.revision ?? 'cur'}|${[...acks].sort().join(',')}|${kind === 'test' ? testUrl.trim() : ''}`;
      const key = (pendingKey.current[sig] ??= crypto.randomUUID());
      const body: any = { kind, revision: val?.revision, ackWarnings: acks, idempotencyKey: key };
      if (kind === 'test' && testUrl.trim()) body.testUrl = testUrl.trim();
      const r = await api.post<{ exportId: string; jobId: string }>(`/api/variants/${v.id}/export`, body);
      delete pendingKey.current[sig];
      const j = await waitForJob(r.jobId, setJob);
      const e = await api.get<ExportRow>(`/api/exports/${r.exportId}`);
      setResult(e);
      if (j.status === 'failed' && !e.check_results?.length) setXErr(j.error ?? 'היצוא נכשל');
      loadHistory();
    } catch (e: any) { setXErr(e.message); } finally { setBusy(false); }
  };

  return (
    <article className="card" aria-labelledby={`xv-${v.id}`}>
      <div className="xc-variant">
        <div className="stack" style={{ alignItems: 'center' }}>
          <VariantThumb ctx={ctx} maxW={240} maxH={220} />
          <Link className="btn small" to={`/variants/${v.id}/edit`}>לעורך</Link>
        </div>
        <div className="stack" style={{ minWidth: 0 }}>
          <div className="row between">
            <h2 id={`xv-${v.id}`} style={{ margin: 0 }}>{v.display_name} <span className="mono muted">{v.width}×{v.height}</span></h2>
            <span className="small muted">{v.media_name} · {v.placement_name} · גרסה <span className="mono">{val?.revision ?? v.current_revision}</span></span>
          </div>
          <div className="row small">
            {spec && <span className={`badge ${approved ? 'ok' : 'warn'}`}>מפרט v{spec.version}: {SPEC_STATUS_LABEL[spec.status] ?? spec.status}{!approved ? ' · טיוטה בלבד' : ''}</span>}
            {spec?.is_demo && <span className="badge">מפרט הדגמה</span>}
            {history?.[0] && <span className={`badge ${XSTATUS[history[0].status]?.[1] ?? ''}`}>יצוא אחרון: {KIND_LABEL[history[0].kind]} · {XSTATUS[history[0].status]?.[0]} · {fmtDate(history[0].created_at)}</span>}
          </div>

          <div className="pgroup">
            <strong className="small">קישור שיינעל ביצוא</strong>
            <div className="mono small" style={{ wordBreak: 'break-all' }}>{link?.resolvedUrl ?? val?.resolvedUrl ?? <span className="muted">לא נוצר קישור מתויג</span>}</div>
            {link && link.status !== 'ok' && <div className="small" style={{ color: 'var(--warn)' }}>סטטוס קישור: {link.status}{link.issues?.length ? ` — ${link.issues.map((i: any) => i.message ?? i).join('; ')}` : ''}</div>}
          </div>

          <div className="row">
            <button className="btn small" onClick={validate} disabled={checking}>{checking ? 'בודק…' : 'הרץ בדיקות'}</button>
            {val && <span className="small">{val.summary.errors} שגיאות · {val.summary.warnings} אזהרות · {val.summary.passed} עברו</span>}
          </div>
          <ErrorBox error={vErr} />
          {val && <ChecksView checks={val.checks} acks={acks} onAck={(c, on) => setAcks((a) => on ? [...new Set([...a, c])] : a.filter((x) => x !== c))} />}

          <div className="pgroup">
            <strong className="small">יצוא</strong>
            <div className="row" style={{ marginTop: 4 }}>
              <button className="btn" onClick={() => runExport('draft')} disabled={busy}>יצוא טיוטה (מסומן)</button>
              <button className="btn primary" onClick={() => runExport('delivery')} disabled={busy || deliveryBlock.length > 0} aria-describedby={`dlv-${v.id}`}>יצוא למסירה</button>
              <input className="mono" style={{ width: 220 }} aria-label="URL בדיקה (רק בחבילת טסט)" placeholder="URL בדיקה (אופציונלי)" value={testUrl} onChange={(e) => setTestUrl(e.target.value)} />
              <button className="btn" onClick={() => runExport('test')} disabled={busy}>חבילת טסט</button>
            </div>
            <div id={`dlv-${v.id}`} className="small" style={{ color: deliveryBlock.length ? 'var(--warn)' : 'var(--ok)' }}>
              {deliveryBlock.length ? <>מסירה אינה זמינה: {deliveryBlock.join(' · ')}</> : 'מוכן ליצוא למסירה.'}
            </div>
            <div className="small muted">חבילת טסט עם URL בדיקה נפרדת ומסומנת — קישורי production אינם משתנים.</div>
            {job && <div className="row small" aria-live="polite"><JobBadge status={job.status} /> {job.error}</div>}
            <ErrorBox error={xErr} />
          </div>
          {result && <ExportResult e={result} />}

          <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) loadHistory(); }}>
            <summary className="small">היסטוריית יצוא ({history?.length ?? '…'})</summary>
            {history && history.length > 0 ? (
              <table className="table">
                <thead><tr><th>זמן</th><th>סוג</th><th>מצב</th><th>גרסה</th><th>ZIP</th><th>חבילה</th><th><span className="sr-only">פעולות</span></th></tr></thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td className="small">{fmtDate(h.created_at)}</td>
                      <td>{KIND_LABEL[h.kind]}</td>
                      <td><span className={`badge ${XSTATUS[h.status]?.[1] ?? ''}`}>{XSTATUS[h.status]?.[0] ?? h.status}</span></td>
                      <td className="mono">r{h.banner_revision}</td>
                      <td className="mono small">{fmtKb(h.size_bytes)}</td>
                      <td className="mono small" style={{ wordBreak: 'break-all' }}>{h.package_name}</td>
                      <td><button className="btn small" onClick={async () => setOpen(await api.get<ExportRow>(`/api/exports/${h.id}`))}>פרטים</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="small muted">אין יצואים קודמים.</div>}
            {open && <ExportResult e={open} />}
          </details>
        </div>
      </div>
    </article>
  );
}
